// Tier 2 (SuPER System) — UI components extracted from LessonsHub.jsx
// Keeps the main hub file under the 700-line readability threshold.
//
// All Tier 2-specific constants, gating logic, milestone gate, certificate
// modal, and decorative placeholders live here. The main LessonsHub still
// owns the unified LessonCard / LessonViewer / QuizModal because Tier 1 and
// Tier 2 share that path.
import React, { useState } from "react";
import { THEME, flexxState } from "./state";
import Portal from "./Portal";
import { FlexxCertificate, openCertificatePrintView } from "./FlexxCertificate";

// ── Tier 2 constants ─────────────────────────────────────────
export const TIER2_PASS_THRESHOLD = 0.75;
export const TIER2_LESSON_XP = 35;       // first-pass XP per Tier 2 lesson
export const TIER2_QUIZ_PASS_BONUS = 5;  // +5 XP per Tier 2 quiz pass
export const TIER2_TIER_BONUS = 125;     // Tier 2 completion bonus
export const TIER2_PHASES = ["2.1", "2.2", "2.3", "2.4", "2.5"];

// ── Header (renders above the Tier 2 module rows) ────────────
export function Tier2Header({ passed, total, unlocked, certified }) {
  return (
    <div data-testid="tier2-header" style={{
      display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap",
      marginTop: 36, marginBottom: 14, paddingTop: 18,
      borderTop: `1px solid ${THEME.border}`,
    }}>
      <div style={{ fontSize: 11, letterSpacing: 2.5, color: THEME.gold, fontFamily: "monospace" }}>
        {unlocked ? "TIER 2 · LESSONS" : "🔒 TIER 2"}
      </div>
      <div style={{ color: THEME.text, fontSize: 22, fontWeight: 800, fontFamily: "'Bebas Neue', 'Oswald', sans-serif", letterSpacing: 0.5 }}>
        INTERMEDIATE — SuPER SYSTEM
      </div>
      {unlocked && (
        <div data-testid="tier2-progress" style={{ marginLeft: "auto", color: THEME.mute, fontFamily: "monospace", fontSize: 12 }}>
          {passed} / {total} passed
        </div>
      )}
      {certified && (
        <div data-testid="tier2-certified-pill" style={{
          marginLeft: unlocked ? 0 : "auto",
          padding: "4px 10px", border: `1px solid ${THEME.gold}`, borderRadius: 999,
          color: THEME.gold, fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5,
          background: `${THEME.gold}10`,
        }}>
          🎓 SuPER CERTIFIED
        </div>
      )}
    </div>
  );
}

// ── Locked-tier placeholder (shown when Tier 1 is not graduated) ──
export function Tier2GatePlaceholder() {
  return (
    <div data-testid="tier2-locked-placeholder" style={{
      padding: 22, background: "#0d1218", border: `1px dashed ${THEME.gold}55`,
      borderLeft: `3px solid ${THEME.gold}`, borderRadius: 10, textAlign: "center",
    }}>
      <div style={{ fontSize: 36, marginBottom: 6 }}>🔒</div>
      <div style={{ color: THEME.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 2, marginBottom: 8 }}>TIER 2 LOCKED</div>
      <div style={{ color: THEME.text, fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: "0 auto" }}>
        Complete Tier 1 to unlock the SuPER System.
      </div>
      <div style={{ color: THEME.mute, fontSize: 12, lineHeight: 1.6, maxWidth: 480, margin: "8px auto 0", fontStyle: "italic" }}>
        25 lessons · 5 modules · ends with the SuPER Certified credential.
      </div>
    </div>
  );
}

// ── Tier 3 preview (shown only after Tier 2 cert is issued) ──
export function Tier3PreviewLocked() {
  return (
    <div data-testid="tier3-preview-locked" style={{
      marginTop: 28, padding: 22, background: "#0d1218", border: `1px dashed ${THEME.border}`,
      borderRadius: 10, textAlign: "center", opacity: 0.85,
    }}>
      <div style={{ fontSize: 28, marginBottom: 6 }}>🔒</div>
      <div style={{ color: THEME.mute, fontFamily: "monospace", fontSize: 10, letterSpacing: 2, marginBottom: 6 }}>TIER 3 · COMING SOON</div>
      <div style={{ color: THEME.text, fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: "0 auto" }}>
        ADVANCED — Live Markets &amp; Edge Building
      </div>
      <div style={{ color: THEME.mute, fontSize: 11, lineHeight: 1.6, marginTop: 8, fontFamily: "monospace" }}>
        UNLOCKS WHEN TIER 3 LESSONS ARE RELEASED
      </div>
    </div>
  );
}

// ── Milestone Gate — self-reported 30 demo trades ────────────
// `tier2Lessons` is the array of normalised Tier 2 lesson objects
// (filtered by parent component for performance + tier-isolation).
export function Tier2MilestoneGate({ tier2Lessons, completion, onConfirm, onClose, modalBg, modalCard, primaryBtn, ghostBtn }) {
  const allLessonsDone = tier2Lessons.every((l) => completion[l.id]?.passed);
  const allQuizzesPassedAt75 = tier2Lessons.every((l) => (completion[l.id]?.score ?? 0) >= TIER2_PASS_THRESHOLD);
  const understandModuleDone = tier2Lessons.filter((l) => l.phase === "2.3").every((l) => completion[l.id]?.passed);

  const [planDownloaded, setPlanDownloaded] = useState(false);
  const [journalSetup, setJournalSetup] = useState(false);

  const items = [
    { id: "lessons", label: "25 lessons completed", done: allLessonsDone, manual: false },
    { id: "quizzes", label: "All module quizzes passed (75% min)", done: allQuizzesPassedAt75, manual: false },
    { id: "understand", label: "u: Understand checklist reviewed (Module 2.3)", done: understandModuleDone, manual: false },
    { id: "plan", label: "Trade plan template downloaded (Lesson 2.4.1)", done: planDownloaded, manual: true, set: setPlanDownloaded },
    { id: "journal", label: "Journal setup confirmed (Lesson 2.5.3)", done: journalSetup, manual: true, set: setJournalSetup },
  ];
  const allReady = items.every((it) => it.done);

  return (
    <Portal>
      <div data-testid="tier2-milestone-gate-modal" style={modalBg}>
        <div style={modalCard}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 10, letterSpacing: 2 }}>TIER 2 · MILESTONE GATE</div>
              <h2 style={{ margin: "6px 0 0", color: THEME.text, fontSize: 24, fontFamily: "'Bebas Neue','Oswald',sans-serif", letterSpacing: 0.5 }}>
                PROVE YOU CAN USE THE SUPER SYSTEM
              </h2>
            </div>
            <button data-testid="tier2-gate-close" onClick={onClose} style={ghostBtn}>Close</button>
          </div>

          <div style={{ display: "grid", gap: 8, marginTop: 6, marginBottom: 16 }}>
            {items.map((it) => (
              <label key={it.id} data-testid={`tier2-gate-item-${it.id}`}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: 12, borderRadius: 8,
                  background: it.done ? "#22C55E10" : "#0d1218",
                  border: `1px solid ${it.done ? "#22C55E" : THEME.border}`,
                  borderLeft: `3px solid ${it.done ? "#22C55E" : THEME.gold}`,
                  cursor: it.manual ? "pointer" : "default",
                }}>
                <input type="checkbox" checked={it.done}
                  disabled={!it.manual}
                  onChange={(e) => it.manual && it.set(e.target.checked)}
                  data-testid={`tier2-gate-check-${it.id}`}
                  style={{ width: 18, height: 18, accentColor: it.done ? "#22C55E" : THEME.gold, cursor: it.manual ? "pointer" : "not-allowed" }} />
                <span style={{ color: it.done ? THEME.text : THEME.mute, fontSize: 13, lineHeight: 1.5, flex: 1 }}>
                  {it.label}
                  {!it.manual && it.done && <span style={{ color: "#22C55E", fontFamily: "monospace", fontSize: 10, marginLeft: 8 }}>· auto-verified</span>}
                </span>
              </label>
            ))}
          </div>

          <div style={{
            padding: 14, marginBottom: 16, borderRadius: 10,
            background: `${THEME.gold}10`, border: `1px solid ${THEME.gold}55`, borderLeft: `3px solid ${THEME.gold}`,
            color: THEME.text, fontSize: 13, lineHeight: 1.7, fontStyle: "italic",
          }}>
            <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 9, letterSpacing: 2, marginBottom: 6, fontStyle: "normal" }}>AXEL — MILESTONE GATE</div>
            "You've learned the SuPER system. Now prove you can use it. Complete 30 demo trades with the full checklist before you call yourself SuPER Certified. The knowledge is yours. The execution is up to you."
          </div>

          <button data-testid="tier2-gate-confirm-btn" onClick={onConfirm}
            disabled={!allReady}
            style={{
              ...primaryBtn, width: "100%", padding: "14px 18px", fontSize: 13,
              opacity: allReady ? 1 : 0.4, cursor: allReady ? "pointer" : "not-allowed",
            }}>
            I'VE COMPLETED MY 30 DEMO TRADES →
          </button>
          {!allReady && (
            <div data-testid="tier2-gate-waiting" style={{ marginTop: 10, color: THEME.mute, fontSize: 11, fontFamily: "monospace", textAlign: "center", letterSpacing: 1 }}>
              Tick all 5 items above to confirm.
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}

// ── Tier 2 Certificate (SuPER Certified — Intermediate) ──────
export function Tier2CertificateModal({ onClose, modalBg, modalCard, primaryBtn, ConfettiComp }) {
  const profile = flexxState.profile() || {};
  const issued = flexxState.tier2Graduate();
  const dateStr = issued
    ? new Date(issued).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "—";
  const signedCert = (flexxState.tierCerts() || {})["2"] || null;
  return (
    <Portal>
      <div data-testid="tier2-certificate-modal">
        {ConfettiComp && <ConfettiComp />}
        <FlexxCertificate
          studentName={profile.name || "FLEXX Trader"}
          courseName="The SuPER System — Pro Trading Mechanics"
          courseSubtitle="An intermediate component of the FLEXX ACADEMY Elite Trading Series"
          completionDate={dateStr}
          verifyCode={signedCert?.cert_id || ""}
          tier="intermediate"
          onClose={onClose}
          onDownload={() => openCertificatePrintView({
            studentName: profile.name || "FLEXX Trader", tier: "intermediate",
            courseName: "The SuPER System — Pro Trading Mechanics",
            courseSubtitle: "An intermediate component of the FLEXX ACADEMY Elite Trading Series",
            completionDate: dateStr, verifyCode: signedCert?.cert_id || "",
          })} />
      </div>
    </Portal>
  );
}

// Re-export the constants object for callers that prefer named lookups.
export const TIER2 = {
  PHASES: TIER2_PHASES,
  PASS_THRESHOLD: TIER2_PASS_THRESHOLD,
  LESSON_XP: TIER2_LESSON_XP,
  QUIZ_PASS_BONUS: TIER2_QUIZ_PASS_BONUS,
  TIER_BONUS: TIER2_TIER_BONUS,
};
